import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Sun, Moon, Eye, EyeOff, Check, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from 'sonner';
import { authAPI } from '../../services/api';

// Password validation rules
const PASSWORD_RULES = {
  length: { regex: /.{8,}/, label: 'At least 8 characters' },
  uppercase: { regex: /[A-Z]/, label: 'At least one uppercase letter' },
  lowercase: { regex: /[a-z]/, label: 'At least one lowercase letter' },
  number: { regex: /[0-9]/, label: 'At least one number' },
  special: { regex: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, label: 'At least one special character' },
};

function PasswordValidator({ password }) {
  const getStatus = (rule) => PASSWORD_RULES[rule].regex.test(password);

  return (
    <div className="space-y-2 mt-3 p-3 bg-muted rounded-md">
      <p className="text-xs font-medium text-muted-foreground">Password requirements:</p>
      {Object.entries(PASSWORD_RULES).map(([key, rule]) => {
        const isMet = getStatus(key);
        return (
          <div key={key} className="flex items-center gap-2 text-xs">
            {isMet ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : (
              <X className="h-4 w-4 text-red-600" />
            )}
            <span className={isMet ? 'text-green-600' : 'text-red-600'}>
              {rule.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    // Validate invite token on mount
    validateInviteToken();
  }, [token]);

  const validateInviteToken = async () => {
    if (!token) {
      setValidating(false);
      setInviteValid(false);
      return;
    }

    try {
      const response = await authAPI.validateInviteToken(token);
      setInviteValid(true);
      setInviteData(response.data);
    } catch (error) {
      toast.error('Invitation link has expired or is invalid. Please contact your administrator.');
      setInviteValid(false);
    } finally {
      setValidating(false);
    }
  };

  const validatePassword = () => {
    return Object.values(PASSWORD_RULES).every(rule => rule.regex.test(password));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePassword()) {
      toast.error('Password does not meet all requirements');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authAPI.setInvitePassword(token, password);
      toast.success('Account created successfully! You can now login.');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!inviteValid) {
    return (
      <div className="min-h-screen flex">
        {/* Left Panel - Image */}
        <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900">
          <img
            src="https://images.unsplash.com/photo-1555949963-aa79dcee981c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxkYXRhJTIwc2VjdXJpdHklMjBhYnN0cmFjdCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcwOTQ4Mjc1fDA&ixlib=rb-4.1.0&q=85"
            alt="Data Security"
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
          <div className="relative z-10 flex flex-col justify-between p-12 text-white">
            <div className="flex items-center gap-3">
              <img
                src="/fulllogo_transparent.png"
                alt="Assura"
                className="h-10 brightness-0 invert"
              />
            </div>
            <div>
              <h1 className="text-4xl font-bold mb-4">Compliance Without Complexity</h1>
              <p className="text-lg text-slate-300">
                Simplify compliance, manage risk, and protect data across your organization with our comprehensive Caribbean data protection compliance platform.
              </p>
            </div>
            <p className="text-sm text-slate-400">
              © 2026 Assura. All rights reserved.
            </p>
          </div>
        </div>

        {/* Right Panel - Error */}
        <div className="flex-1 flex items-center justify-center p-8 bg-background">
          <div className="absolute top-4 right-4">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>

          <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-2xl font-bold">Link Expired</CardTitle>
              <CardDescription>
                This invitation link has expired or is invalid.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Please contact your administrator to send you a new invitation link. Invitation links expire after 72 hours.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full"
                data-testid="back-to-login-btn"
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1555949963-aa79dcee981c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxkYXRhJTIwc2VjdXJpdHklMjBhYnN0cmFjdCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcwOTQ4Mjc1fDA&ixlib=rb-4.1.0&q=85"
          alt="Data Security"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img
              src="/fulllogo_transparent.png"
              alt="Assura"
              className="h-10 brightness-0 invert"
            />
          </div>
          <div>
            <h1 className="text-4xl font-bold mb-4">Compliance Without Complexity</h1>
            <p className="text-lg text-slate-300">
              Simplify compliance, manage risk, and protect data across your organization with our comprehensive Caribbean data protection compliance platform.
            </p>
          </div>
          <p className="text-sm text-slate-400">
            © 2026 Assura. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="absolute top-4 right-4">
          <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>

        <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-2 lg:hidden mb-4">
              <img
                src="/fulllogo_transparent.png"
                alt="Assura"
                className="h-8 dark:brightness-0 dark:invert"
              />
            </div>
            <CardTitle className="text-2xl font-bold">Create your account</CardTitle>
            <CardDescription>
              {inviteData ? (
                <>
                  Welcome, <strong>{inviteData.full_name}</strong>. Set your password to get started.
                </>
              ) : (
                'Set your password to access Assura'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {inviteData && (
                <div className="space-y-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-md border border-blue-200 dark:border-blue-800">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Account Information</p>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Name:</strong> {inviteData.full_name}
                  </p>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Email:</strong> {inviteData.email}
                  </p>
                  {inviteData.organization_name && (
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      <strong>Organization:</strong> {inviteData.organization_name}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="set-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {password && <PasswordValidator password={password} />}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="set-confirm-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    data-testid="toggle-confirm-password-visibility"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading || !validatePassword() || password !== confirmPassword}
                data-testid="set-password-submit-btn"
              >
                {loading ? 'Creating account...' : 'Create account'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
